/**
 * lumosity.js — client for lumosity_infra (the Implied app server).
 *
 * Auth is a Clerk session JWT minted with the `implied_backend` JWT template
 * (same template the Implied web app uses — it carries the email/org_role/
 * org_name claims the server's watchlist route requires). Callers pass
 * `getClerkToken` from useLumosityAuth(); every request mints a fresh token
 * because Clerk session tokens expire after ~60s.
 *
 * The integration is opt-in: it activates only when BOTH env vars are set.
 *   VITE_CLERK_PUBLISHABLE_KEY — pk_live_… (prod Clerk instance, clerk.implied.com)
 *                                or pk_test_… (dev instance) for local dev
 *   VITE_LUMOSITY_API_BASE    — prod app server URL, or the dev server
 *                               (https://implied-server-dev-….us-west1.run.app)
 *
 * NOTE: sales.implied.com must be in lumosity_infra's CORS allowlist
 * (web_server.py) — the browser calls the server directly, no proxy.
 */

const BASE = import.meta.env.VITE_LUMOSITY_API_BASE ?? '';

export const LUMOSITY_ENABLED = Boolean(
  BASE && import.meta.env.VITE_CLERK_PUBLISHABLE_KEY
);

async function req(getClerkToken, path, options = {}) {
  const token = await getClerkToken();
  if (!token) throw new Error('Not signed in to Lumosity');
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers ?? {}),
    },
  });
  let body = null;
  try { body = await res.json(); } catch (_) {}
  if (!res.ok) {
    if (res.status === 403) throw new Error('Not authorized (needs lumosity org membership)');
    throw new Error(body?.error ?? body?.message ?? res.statusText ?? `HTTP ${res.status}`);
  }
  return body;
}

/** Fetch a user's default watchlist tickers. Returns string[] (may be empty). */
export async function fetchUserWatchlist(getClerkToken, userEmail) {
  const data = await fetchUserWatchlistInfo(getClerkToken, userEmail);
  return data.tickers;
}

/**
 * Default watchlist tickers plus the server-derived overall sector
 * (IMP-1843): { tickers: string[], sector: string|null, sectorPack: string|null }.
 * sector is the dominant implied_universe sector of the list (> 60% of
 * tickers), sectorPack the sales pack it rolls up to; both null when the
 * coverage has no dominant sector (e.g. the S&P 500 default list).
 */
export async function fetchUserWatchlistInfo(getClerkToken, userEmail) {
  const data = await req(
    getClerkToken,
    `/v1/user/watchlist?user=${encodeURIComponent(userEmail.toLowerCase())}`
  );
  return {
    tickers: data?.tickers ?? [],
    sector: data?.watchlist_overall_sector ?? null,
    sectorPack: data?.sector_pack_mapping ?? null,
  };
}

/** Replace a user's default watchlist with the given tickers. */
export function updateUserWatchlist(getClerkToken, userEmail, tickers) {
  return req(getClerkToken, '/v1/user/watchlist', {
    method: 'POST',
    body: JSON.stringify({ user: userEmail.toLowerCase(), watchlist_tickers: tickers }),
  });
}

/**
 * Create an Implied product user (Clerk account + org-from-domain + default
 * watchlist + workspaces) — the same POST /v1/user/create the /admin Create
 * User page calls. Authorized for org:admin or anyone in the lumosity org.
 * Returns { success, message, clerk_user_id }; the caller flags the new user
 * as on-trial via the sales-dashboard backend (apiFlagClerkUser), since this
 * endpoint doesn't set lifecycle metadata (yet — lumosity_infra IMP-1804).
 */
/**
 * Per-category data counts for a copy: { old: {...}, new: {...} }, each with
 * exists_in_clerk, chats, watchlists, workspaces, workflows, datasheets,
 * vault_notes, proteges[]. Lets the UI show what a copy would bring over.
 */
export function precheckCopyUser(getClerkToken, oldEmail, newEmail) {
  return req(getClerkToken, '/v1/admin/copy-user/precheck', {
    method: 'POST',
    body: JSON.stringify({
      old_email: oldEmail.toLowerCase(),
      new_email: newEmail.toLowerCase(),
    }),
  });
}

/**
 * Create an Implied product user as a COPY of an existing user — the same
 * POST /v1/admin/copy-user the /admin Copy / Merge User tab calls. With
 * create_user set, the endpoint creates the destination Clerk account first
 * (no seeded watchlist/workspaces — the copy supplies the data), then copies
 * everything from sourceEmail (mode "replace": destination starts empty).
 * on_trial flags lifecycle=trial at creation (infra PR #2833; harmlessly
 * ignored before it deploys — the caller flags via apiFlagClerkUser anyway).
 */
export function copyProductUser(getClerkToken, { sourceEmail, newEmail, firstName, lastName, password }) {
  return req(getClerkToken, '/v1/admin/copy-user', {
    method: 'POST',
    body: JSON.stringify({
      old_email: sourceEmail.toLowerCase(),
      new_email: newEmail.toLowerCase(),
      mode: 'replace',
      create_user: { first_name: firstName, last_name: lastName, password, on_trial: true },
    }),
  });
}

export function createProductUser(getClerkToken, { firstName, lastName, email, password, useSp500 = true, watchlistTickers = [] }) {
  return req(getClerkToken, '/v1/user/create', {
    method: 'POST',
    body: JSON.stringify({
      first_name: firstName,
      last_name: lastName,
      email: email.toLowerCase(),
      password,
      // Same semantics as /admin Create User: S&P 500 by default, or a
      // custom ticker list (use_sp500 wins server-side when both are sent).
      use_sp500: useSp500,
      ...(watchlistTickers.length > 0 ? { watchlist_tickers: watchlistTickers } : {}),
      on_trial: true, // server stamps lifecycle=trial once infra PR #2833 deploys; ignored before then
    }),
  });
}
